import * as tslib_1 from "tslib";
import { BehaviorSubject } from 'rxjs';
import { Cache } from 'aws-amplify';
import { environment } from '../../../environments/environment';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfigService } from '../config';
import { ViewAsScopeService } from '../view-as-scope';
import { VoucherDemoModeService } from '../vouchers/demo/voucher-demo-mode.service';
import { applyVoucherDemoUserAttributes, VOUCHER_DEMO_VIEW_ROLE } from '../vouchers/demo/voucher-demo.constants';
import { isImpersonationRuntimeEnabled } from './impersonation-runtime';
import * as i0 from "@angular/core";
import * as i1 from "ngx-permissions";
import * as i2 from "../view-as-scope/view-as-scope.service";
import * as i3 from "../config/config.service";
import * as i4 from "../vouchers/demo/voucher-demo-mode.service";
var STORAGE_KEY = 'dev_impersonation_token';
var DevImpersonationService = /** @class */ (function () {
    function DevImpersonationService(permissionsService, viewAsScopeService, configService, voucherDemoMode) {
        var _this = this;
        this.permissionsService = permissionsService;
        this.viewAsScopeService = viewAsScopeService;
        this.configService = configService;
        this.voucherDemoMode = voucherDemoMode;
        this.tokenSubject = new BehaviorSubject(null);
        this.token$ = this.tokenSubject.asObservable();
        this.impersonationEnabled = false;
        this.devSwitcherEnabled = false;
        this.cacheIntercepted = false;
        this.devSwitcherEnabled = !environment.production;
        this.originalCacheGetItem = Cache.getItem.bind(Cache);
        if (this.configService.isLoaded()) {
            this.enableForRuntime(this.configService.get('host'));
        }
        this.token$.subscribe(function (token) {
            if (_this.voucherDemoMode.isEnabled()) {
                _this.applyDemoRetailPermissions();
                return;
            }
            if (token) {
                _this.applyPermissions(token);
            }
            else {
                _this.clearPermissions();
            }
        });
    }
    /**
     * Turn impersonation on after config.json is loaded. Safe to call more than once.
     * Production Angular builds only enable this when host is local or staging.
     */
    DevImpersonationService.prototype.enableForRuntime = function (host) {
        this.devSwitcherEnabled = !environment.production;
        this.voucherDemoMode.enableForRuntime(host);
        if (!isImpersonationRuntimeEnabled(environment.production, host)) {
            this.impersonationEnabled = false;
            return;
        }
        this.impersonationEnabled = true;
        this.interceptCache();
        this.restorePersistedToken();
        if (this.voucherDemoMode.isEnabled()) {
            this.applyDemoRetailPermissions();
        }
    };
    /**
     * Intercept Cache.getItem to apply impersonation overrides
     * This ensures components reading directly from Cache get impersonated values
     */
    DevImpersonationService.prototype.interceptCache = function () {
        var _this = this;
        if (this.cacheIntercepted) {
            return;
        }
        this.cacheIntercepted = true;
        Cache.getItem = function (key) {
            var originalValue = _this.originalCacheGetItem(key);
            if (key === 'userAttr' && originalValue) {
                return _this.getUserAttributes();
            }
            return originalValue;
        };
    };
    /**
     * Full local Dev Mode switcher. Never enabled in production Angular builds.
     */
    DevImpersonationService.prototype.isDevMode = function () {
        return this.devSwitcherEnabled;
    };
    /**
     * Super-admin impersonation for local and staging hosts. Never enabled when
     * config.json host is prod.
     */
    DevImpersonationService.prototype.isImpersonationEnabled = function () {
        return this.impersonationEnabled;
    };
    /**
     * Get current impersonation token
     */
    DevImpersonationService.prototype.getToken = function () {
        var token = this.tokenSubject.value;
        if (token && this.isTokenExpired(token)) {
            this.clearToken();
            return null;
        }
        return token;
    };
    /**
     * Set impersonation token
     */
    DevImpersonationService.prototype.setToken = function (tokenData) {
        if (!this.impersonationEnabled) {
            return;
        }
        this.tokenSubject.next(tokenData);
        this.saveToStorage(tokenData);
    };
    /**
     * Clear impersonation token
     */
    DevImpersonationService.prototype.clearToken = function () {
        if (!this.impersonationEnabled) {
            return;
        }
        this.tokenSubject.next(null);
        this.clearStorage();
    };
    /**
     * Check if token is expired
     */
    DevImpersonationService.prototype.isTokenExpired = function (token) {
        return Date.now() >= token.expiresAt * 1000;
    };
    /**
     * Get impersonation token for headers (returns null if expired)
     */
    DevImpersonationService.prototype.getTokenForHeader = function () {
        var token = this.getToken();
        return token ? token.token : null;
    };
    /**
     * Read the stored JWT for Amplify custom headers. The prod Amplify endpoint
     * never calls this; local and staging do, including production Angular builds
     * on the staging portal.
     */
    DevImpersonationService.getTokenFromStorage = function () {
        try {
            var stored = sessionStorage.getItem(STORAGE_KEY);
            if (!stored) {
                return null;
            }
            var tokenData = JSON.parse(stored);
            if (tokenData.expiresAt && Date.now() >= tokenData.expiresAt * 1000) {
                sessionStorage.removeItem(STORAGE_KEY);
                return null;
            }
            return tokenData.token || null;
        }
        catch (e) {
            return null;
        }
    };
    /**
     * Save token to sessionStorage
     */
    DevImpersonationService.prototype.saveToStorage = function (token) {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(token));
        }
        catch (e) {
            // Silently fail - sessionStorage may not be available
        }
    };
    /**
     * Load token from sessionStorage
     */
    DevImpersonationService.prototype.loadFromStorage = function () {
        try {
            var stored = sessionStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        }
        catch (e) {
            return null;
        }
    };
    DevImpersonationService.prototype.restorePersistedToken = function () {
        if (this.tokenSubject.value) {
            return;
        }
        var saved = this.loadFromStorage();
        if (saved && !this.isTokenExpired(saved) && saved.token) {
            this.tokenSubject.next(saved);
            return;
        }
        if (saved) {
            this.clearStorage();
        }
    };
    /**
     * Clear token from sessionStorage
     */
    DevImpersonationService.prototype.clearStorage = function () {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        }
        catch (e) {
            // Silently fail - sessionStorage may not be available
        }
    };
    /**
     * Get user attributes with override applied (backward compatibility)
     */
    DevImpersonationService.prototype.getUserAttributes = function () {
        if (this.voucherDemoMode.isEnabled()) {
            var originalAttr = this.viewAsScopeService.getOriginalUserAttributes()
                || this.originalCacheGetItem('userAttr');
            if (!originalAttr) {
                return originalAttr;
            }
            return applyVoucherDemoUserAttributes(originalAttr);
        }
        // View As scope takes precedence (works in production)
        if (this.viewAsScopeService.isEnabled()) {
            return this.viewAsScopeService.getEffectiveUserAttributes();
        }
        // Get original user attributes
        var userAttr = this.originalCacheGetItem('userAttr');
        if (!this.impersonationEnabled || !userAttr) {
            return userAttr;
        }
        // Get override from impersonation token
        var token = this.getToken();
        if (!token) {
            return userAttr;
        }
        // Create a copy with overridden values
        var overriddenAttr = tslib_1.__assign({}, userAttr);
        overriddenAttr['custom:role'] = token.role;
        if (token.role === 'campusadmin' && token.portalView === 'retail') {
            overriddenAttr['custom:portal_view'] = 'retail';
        }
        else {
            overriddenAttr['custom:portal_view'] = 'ops';
        }
        // Override IDs based on impersonation token
        if (token.distiId !== null && token.distiId !== undefined) {
            overriddenAttr['custom:disti_id'] = token.distiId;
        }
        else {
            overriddenAttr['custom:disti_id'] = null;
        }
        if (token.marketId !== null && token.marketId !== undefined) {
            overriddenAttr['custom:market_id'] = token.marketId;
        }
        else {
            overriddenAttr['custom:market_id'] = null;
        }
        if (token.clientId !== null && token.clientId !== undefined) {
            overriddenAttr['custom:client_id'] = token.clientId;
        }
        else {
            overriddenAttr['custom:client_id'] = null;
        }
        if (token.campusId !== null && token.campusId !== undefined) {
            overriddenAttr['custom:campus_id'] = token.campusId;
        }
        else {
            overriddenAttr['custom:campus_id'] = null;
        }
        return overriddenAttr;
    };
    /**
     * Get role with override applied (backward compatibility)
     */
    DevImpersonationService.prototype.getRole = function () {
        if (this.voucherDemoMode.isEnabled()) {
            return VOUCHER_DEMO_VIEW_ROLE;
        }
        // View As scope takes precedence (works in production)
        if (this.viewAsScopeService.isEnabled()) {
            var scope = this.viewAsScopeService.getScope();
            return scope && scope.role != null ? scope.role : null;
        }
        if (this.impersonationEnabled) {
            var token = this.getToken();
            if (token) {
                return token.role;
            }
        }
        // Fall back to original user role
        var userAttr = this.originalCacheGetItem('userAttr');
        return userAttr ? userAttr['custom:role'] : null;
    };
    /**
     * Get original user attributes (without override applied)
     * Useful for checking actual user permissions
     */
    DevImpersonationService.prototype.getOriginalUserAttributes = function () {
        return this.originalCacheGetItem('userAttr');
    };
    /**
     * Get original user role (without override applied)
     * Uses ViewAsScopeService which captured the real Cache before any patches,
     * so this always returns the actual role even when View As is enabled.
     */
    DevImpersonationService.prototype.getOriginalRole = function () {
        var userAttr = this.viewAsScopeService.getOriginalUserAttributes();
        return userAttr ? userAttr['custom:role'] : null;
    };
    DevImpersonationService.prototype.applyDemoRetailPermissions = function () {
        this.permissionsService.flushPermissions();
        this.permissionsService.addPermission(VOUCHER_DEMO_VIEW_ROLE);
    };
    /**
     * Apply permissions based on impersonation token
     */
    DevImpersonationService.prototype.applyPermissions = function (token) {
        if (token.role) {
            this.permissionsService.flushPermissions();
            this.permissionsService.addPermission(token.role);
        }
    };
    /**
     * Clear permissions and restore original
     */
    DevImpersonationService.prototype.clearPermissions = function () {
        var userAttr = this.originalCacheGetItem('userAttr');
        if (userAttr && userAttr['custom:role']) {
            this.permissionsService.flushPermissions();
            this.permissionsService.addPermission(userAttr['custom:role']);
        }
    };
    DevImpersonationService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function DevImpersonationService_Factory() { return new DevImpersonationService(i0.ɵɵinject(i1.NgxPermissionsService), i0.ɵɵinject(i2.ViewAsScopeService), i0.ɵɵinject(i3.ConfigService), i0.ɵɵinject(i4.VoucherDemoModeService)); }, token: DevImpersonationService, providedIn: "root" });
    return DevImpersonationService;
}());
export { DevImpersonationService };
